import { Payments } from '@prisma/client';
import { useMutation } from '@tanstack/react-query';
import { refundPayment } from '../../services/payments';
import {
  getRefundPaymentSchema,
  RefundPaymentPayload,
  RefundPaymentResponse,
} from '../../types/payments';
import { getPaymentMaxRefundAmount } from '../../helpers/payments';
import { invalidateOrders } from '../../services/orders';

function useRefundPayment({
  payment,
  disabled,
  onError,
  onSuccess,
}: {
  payment: Payments;
  disabled?: boolean;
  onSuccess?: (waiver: RefundPaymentResponse | null) => void | Promise<void>;
  onError?: (error: Error) => void | Promise<void>;
}) {
  const mutation = useMutation({
    mutationFn: async (payload: RefundPaymentPayload) => {
      if (!payment || disabled) return null;

      const refundPaymentSchema = getRefundPaymentSchema(
        getPaymentMaxRefundAmount(payment),
      );

      const body = await refundPaymentSchema.noUnknown().validate(payload, {
        stripUnknown: true,
      });

      const data: RefundPaymentPayload = {
        ...body,
        amount: Math.floor(body.amount * 100),
      };

      const response = await refundPayment(payment.id, data);

      if (response?.orderId) {
        await invalidateOrders();
      }

      return response;
    },
    onSuccess,
    onError,
  });

  return {
    mutation,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
}

export default useRefundPayment;
